import { useEffect, useState } from 'react';

/**
 * Per-device meal-photo thumbnail size for the food-log views. Mirrors
 * `useTextSize`: persists to localStorage and drives a CSS variable
 * (`--meal-thumb`) on `<html>` so the food-log thumbnails resize the instant
 * the preference changes, with no React state threading between the Profile
 * control and the log surfaces.
 *
 * Per-device (not per-profile), like Theme + Text size — a clinician on a 4K
 * monitor wants different sizing than on a laptop.
 */

export type MealThumbnailSize = 'small' | 'medium' | 'large';

const STORAGE_KEY = 'ui.mealThumbnailSize';
const DEFAULT_SIZE: MealThumbnailSize = 'medium';

/** Square thumbnail edge in px per size. Sized up after testing: Small is the
 *  old Large (64), Large is 2× that (128), Medium the midpoint. */
export const MEAL_THUMB_PX: Record<MealThumbnailSize, number> = {
  small: 64,
  medium: 96,
  large: 128,
};

const isSize = (v: unknown): v is MealThumbnailSize =>
  v === 'small' || v === 'medium' || v === 'large';

const readStored = (): MealThumbnailSize => {
  if (typeof window === 'undefined') return DEFAULT_SIZE;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (isSize(raw)) return raw;
  } catch {
    /* private mode — fall through */
  }
  return DEFAULT_SIZE;
};

const writeStored = (value: MealThumbnailSize) => {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
};

const applyToDocument = (value: MealThumbnailSize) => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--meal-thumb', `${MEAL_THUMB_PX[value]}px`);
};

export const useMealThumbnailSize = (): [MealThumbnailSize, (next: MealThumbnailSize) => void] => {
  const [size, setSize] = useState<MealThumbnailSize>(() => readStored());

  // Apply on mount AND whenever it changes so the CSS var stays in sync —
  // multiple components share the DOM state via the variable write.
  useEffect(() => {
    applyToDocument(size);
    writeStored(size);
  }, [size]);

  return [size, setSize];
};

/** Apply the stored size as early as possible (before the food log paints). */
export const useApplyStoredMealThumbnailSize = (): void => {
  useEffect(() => {
    applyToDocument(readStored());
  }, []);
};
